// Layout selectors (presentation contract section 3.1). They name observation data only.

export type Selector =
  | { kind: 'zone'; id: string }
  | { kind: 'zonePrefix'; prefix: string }
  | { kind: 'zonesOwner'; seat: string }
  | { kind: 'zonesKind'; zoneKind: string }
  | { kind: 'countersOwner'; seat: string }
  | { kind: 'countersKey'; prefix: string }
  | { kind: 'countersId'; prefix: string; exact: boolean }
  | { kind: 'board'; id: string }
  | { kind: 'rest' }
  | { kind: 'events' }
  | { kind: 'counter'; id: string };

const SEAT = /^(\{seat\}|\{self\}|[A-Za-z0-9][A-Za-z0-9:_.\-]{0,127})$/;
const ID = /^[A-Za-z0-9{][A-Za-z0-9:_.\-{}]{0,127}$/;

export function parseSelector(text: string): Selector | string {
  if (text === 'rest') return { kind: 'rest' };
  if (text === 'events') return { kind: 'events' };
  if (text.startsWith('counter:')) { const id = text.slice(8); return ID.test(id) ? { kind: 'counter', id } : `Invalid counter in "${text}".`; }
  if (text.startsWith('zones:owner=')) { const seat = text.slice(12); return SEAT.test(seat) ? { kind: 'zonesOwner', seat } : `Invalid seat in "${text}".`; }
  if (text.startsWith('zones:kind=')) return { kind: 'zonesKind', zoneKind: text.slice(11) };
  if (text.startsWith('counters:owner=')) { const seat = text.slice(15); return SEAT.test(seat) ? { kind: 'countersOwner', seat } : `Invalid seat in "${text}".`; }
  if (text.startsWith('counters:key=')) {
    const rest = text.slice(13);
    return rest.endsWith('*') ? { kind: 'countersKey', prefix: rest.slice(0, -1) } : { kind: 'countersKey', prefix: rest };
  }
  if (text.startsWith('counters:id=')) {
    // Counters whose ID starts with a prefix (`counters:id={seat}:resource:*`); without `*`, one exact ID.
    const rest = text.slice(12);
    const prefix = rest.endsWith('*') ? rest.slice(0, -1) : rest;
    return ID.test(prefix) ? { kind: 'countersId', prefix, exact: !rest.endsWith('*') } : `Invalid counter prefix in "${text}".`;
  }
  if (text.startsWith('board:')) { const id = text.slice(6); return ID.test(id) ? { kind: 'board', id } : `Invalid board in "${text}".`; }
  if (text.startsWith('zone:')) {
    const id = text.slice(5);
    if (id.endsWith('*')) return { kind: 'zonePrefix', prefix: id.slice(0, -1) };
    return ID.test(id) ? { kind: 'zone', id } : `Invalid zone in "${text}".`;
  }
  return `Unknown selector "${text}".`;
}

export function usesSeat(selector: Selector): boolean {
  const text = 'id' in selector ? selector.id : 'prefix' in selector ? selector.prefix : 'seat' in selector ? selector.seat : '';
  return text.includes('{seat}');
}

export interface SelectableZone { id: string; kind: string; owner: string | null; boardId: string | null }
export interface SelectableCounter { id: string; owner: string | null; key: string | null }

function substitute(text: string, seat: string | null, self: string | null): string | null {
  if (text.includes('{seat}') && !seat) return null;
  if (text.includes('{self}') && !self) return null;
  return text.replaceAll('{seat}', seat ?? '').replaceAll('{self}', self ?? '');
}

export function selectZones<Z extends SelectableZone>(selector: Selector, zones: Z[], seat: string | null, self: string | null, placed: Set<string>): Z[] {
  switch (selector.kind) {
    case 'zone': { const id = substitute(selector.id, seat, self); return zones.filter(z => z.id === id); }
    case 'zonePrefix': { const p = substitute(selector.prefix, seat, self); return p === null ? [] : zones.filter(z => z.id.startsWith(p)); }
    case 'zonesOwner': { const s = substitute(selector.seat, seat, self); return zones.filter(z => z.owner !== null && z.owner === s); }
    case 'zonesKind': return zones.filter(z => z.kind === selector.zoneKind);
    case 'board': { const id = substitute(selector.id, seat, self); return zones.filter(z => z.kind === 'board' && z.boardId === id); }
    case 'rest': return zones.filter(z => !placed.has(z.id));
    default: return [];
  }
}

export function selectCounters<C extends SelectableCounter>(selector: Selector, counters: C[], seat: string | null, self: string | null): C[] {
  switch (selector.kind) {
    case 'countersOwner': { const s = substitute(selector.seat, seat, self); return counters.filter(c => c.owner !== null && c.owner === s); }
    case 'countersKey': { const p = substitute(selector.prefix, seat, self); return p === null ? [] : counters.filter(c => c.key !== null && c.key.startsWith(p)); }
    case 'counter': { const id = substitute(selector.id, seat, self); return counters.filter(c => c.id === id); }
    case 'countersId': { const p = substitute(selector.prefix, seat, self); return p === null ? [] : counters.filter(c => (selector.exact ? c.id === p : c.id.startsWith(p))); }
    default: return [];
  }
}
